// ignore-string-externalization

import React from 'react';
import styled from 'styled-components';
import {
  IconPlay,
  Type,
  spacer4,
  spacer8,
  spacer16,
  spacer20,
  spacer32,
  spacer40,
  spacer48,
  spacer56,
  spacer72,
  spacer80,
  screenXxsMax,
  screenXsMax,
  screenSmMax,
  screenMdMin,
  gray7,
  heading2FontSize,
  heading2LineHeightSmall,
  cta1FontWeight,
  heading4FontSize,
  heading4LineHeight,
  white,
} from '@spotify-internal/encore-web';
import { itemToSearchHit } from '../../Search';
import type { SearchHit } from '../../Search/types';
import { ModuleType } from '../types';

import { Link } from '../../ui';

import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { hexToRgba } from '../../../../features/src/color-conversion';
import { ImageWrapper } from '../../ContentGrid/index.styles';
import { transformScaleUp } from '../../utils';
import { useLocale } from '@spotify-internal/i18n-core';

const RelatedStoriesList = styled.div`
  padding: ${spacer40};
  background: ${(props: { themeName: string }) =>
    props.themeName === 'whiteOnBlack' ? gray7 : 'unset'};
  color: ${({ theme }) => theme.box.text};

  @media (max-width: ${screenXsMax}) {
    padding: ${spacer16} ${spacer16} ${spacer40};
  }
`;

const StyledSupertext = styled.div`
  padding-bottom: ${spacer40};
  font-size: ${heading2FontSize};
  line-height: ${heading2LineHeightSmall};
  font-weight: ${cta1FontWeight};
  letter-spacing: -1.28px;

  @media (max-width: ${screenXxsMax}) {
    padding: ${spacer16} 0;
  }
`;

const ItemWrapper = styled(Link)`
  display: flex;
  flex-direction: row;
  padding-bottom: ${spacer32};

  :last-child {
    padding-bottom: 0;
  }

  @media (max-width: ${screenXxsMax}) {
    padding-bottom: ${spacer16};
  }

  &:hover {
    h1 {
      color: ${props => hexToRgba(props.theme.link?.color || white, 0.75)};
    }
    img {
      ${transformScaleUp};
      transition: transform 0.3s ease-in-out;
    }
    svg {
      ${transformScaleUp};
      transition: transform 0.3s ease-in-out;
      opacity: 0.8;
    }
  }
`;

const Title = styled(Type.h1).attrs({
  paddingBottom: '0.75em',
  variant: 'alto',
})`
  padding-bottom: ${spacer16};
  max-height: ${spacer40};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: ${screenSmMax}) {
    max-height: ${spacer80};
    -webkit-line-clamp: 2;
  }

  @media (max-width: ${screenXsMax}) {
    max-height: ${spacer72};
  }

  @media (max-width: ${screenXxsMax}) {
    font-size: ${heading4FontSize};
    line-height: ${heading4LineHeight};
    max-height: ${spacer48};
    -webkit-line-clamp: 2;
  }
`;

const Description = styled(Type.h1).attrs({
  variant: 'cello',
})`
  max-height: ${spacer56};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: ${screenSmMax}) {
    display: none;
  }
`;

const ImageContainer = styled(ImageWrapper)`
  width: 20%;
  height: unset;
  overflow: hidden;

  @media (max-width: ${screenMdMin}) {
    width: 40%;
  }

  @media (max-width: ${screenXxsMax}) {
    width: 35%;

    svg {
        height: ${spacer20};
        width: ${spacer20};
        bottom: ${spacer4};
        left: ${spacer4};
      }
    }
  }
`;

const TextWrapper = styled.div`
  padding-left: ${spacer40};
  width: 70%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${({ theme }) => theme.box.text};

  @media (max-width: ${screenMdMin}) {
    padding-left: ${spacer16};
    width: 55%;
  }

  @media (max-width: ${screenXxsMax}) {
    padding-left: ${spacer8};
    width: 55%;
  }
`;

type RelatedStoriesListModuleType = ModuleType & {
  headline?: string;
  items?: SearchHit[];
};

const TRACK_CAT = 'module-relatedStoriesModule';

export const RelatedStoriesListModule: React.FC<
  RelatedStoriesListModuleType
> = ({ headline = '', items = [], themeName = 'whiteOnBlack' }) => {
  const locale = useLocale();
  const relatedStories = (
    items.map(item => itemToSearchHit(item, locale)) as NonNullable<SearchHit>[]
  ).filter(Boolean);

  return (
    (relatedStories?.length && (
      <RelatedStoriesList themeName={themeName}>
        <StyledSupertext>{headline}</StyledSupertext>
        {relatedStories.map((item, index) => (
          <ItemWrapper
            key={`link-${item.id}-${index}`}
            track={[TRACK_CAT, 'Related Stories']}
            href={item.url}
          >
            <ImageContainer
              video={item.contentType === 'videoPage'}
              featured={false}
            >
              {item.image && (
                <ContentfulImage asset={item.image}>
                  <IconPlay aria-hidden />
                </ContentfulImage>
              )}
            </ImageContainer>
            <TextWrapper key={`text-${item.id}-${index}`}>
              <Title>{item.title}</Title>
              <Description>{item.description}</Description>
            </TextWrapper>
          </ItemWrapper>
        ))}
      </RelatedStoriesList>
    )) || <></>
  );
};
